import { useEffect, useRef, useState } from "react";
import { Check, Monitor, Moon, Sun } from "lucide-react";

import { useTheme, type ThemePreference } from "../theme/ThemeProvider";

const OPTIONS: { choice: ThemePreference; label: string; Icon: typeof Sun }[] = [
  { choice: "light", label: "Light", Icon: Sun },
  { choice: "dark", label: "Dark", Icon: Moon },
  { choice: "device", label: "Device", Icon: Monitor },
];

const ICONS: Record<ThemePreference, typeof Sun> = {
  light: Sun,
  dark: Moon,
  device: Monitor,
};

function labelFor(preference: ThemePreference): string {
  return preference.charAt(0).toUpperCase() + preference.slice(1);
}

export function ThemeSelector() {
  const { preference, setPreference } = useTheme();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const ActiveIcon = ICONS[preference];

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Focus the selected option when the menu opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    const selectedIndex = OPTIONS.findIndex((option) => option.choice === preference);
    optionRefs.current[selectedIndex >= 0 ? selectedIndex : 0]?.focus();
  }, [open, preference]);

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const activeIndex = optionRefs.current.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex = activeIndex;
    if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = OPTIONS.length - 1;
    } else {
      const direction = event.key === "ArrowDown" ? 1 : -1;
      nextIndex = (activeIndex + direction + OPTIONS.length) % OPTIONS.length;
    }
    optionRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="theme-selector" ref={containerRef}>
      <button
        className="icon-button"
        type="button"
        aria-label={`Theme: ${labelFor(preference)}`}
        title={`Theme: ${labelFor(preference)}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <ActiveIcon aria-hidden />
      </button>
      <div className="theme-menu" role="menu" hidden={!open} onKeyDown={handleMenuKeyDown}>
        {OPTIONS.map((option, index) => (
          <button
            key={option.choice}
            ref={(node) => {
              optionRefs.current[index] = node;
            }}
            className="theme-option"
            type="button"
            role="menuitemradio"
            aria-checked={preference === option.choice}
            onClick={() => {
              setPreference(option.choice);
              setOpen(false);
            }}
          >
            <option.Icon aria-hidden />
            <span>{option.label}</span>
            <Check className="theme-check" aria-hidden />
          </button>
        ))}
      </div>
    </div>
  );
}
